import { createContext } from 'react';
import type { PartnerDelegationInfo } from '../../identity/types';
import type { EnterClientSummary } from '../types';

/**
 * Where this browser TAB stands with respect to working inside a client company.
 * - `none`: not working inside a client.
 * - `active`: this tab and the server agree on the client, the access has not ended and has not run out.
 * - `otherWindow`: the server's session is for a different client than this tab was working in (another window opened one). This tab's requests are refused, so it must not look as if it works.
 * - `ended`: the server no longer considers the access valid (revoked, suspended, reduced, the person removed).
 * - `expired`: the 10-minute token ran out. Only the server-controlled enter step can give another.
 */
export type ClientSessionStatus = 'none' | 'active' | 'otherWindow' | 'ended' | 'expired';

export interface ClientSessionValue {
  status: ClientSessionStatus;
  delegation: PartnerDelegationInfo | null;
  /** A short message when renewing or opening a client failed. */
  notice: string | null;
  enter: (organizationId: string, relationshipId: string) => Promise<EnterClientSummary>;
  exit: () => Promise<void>;
  /** Re-enters the same client: the server repeats every check and issues a fresh 10-minute token. */
  renew: () => Promise<void>;
  /** This tab takes up the client the browser now has open (after another window changed it). */
  adoptCurrentClient: () => void;
}

export const ClientSessionContext = createContext<ClientSessionValue | null>(null);
